import React, { useEffect, useState, useCallback } from "react";
import useCustomizerStore from "../../../state/customizer";
import useTransitionStore from "../../../state/transitions";
import checkmark from "../../../assets/images/Check.svg";
import {
  fetchCustomizerData,
  getSelectedColorIndex,
  getSelectedWheelIndex,
  setSelectedColorIndex,
  setSelectedWheelIndex,
} from "../../../util/customizerData";
import { isMobile } from "react-device-detect";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import styled from "styled-components";
import { colorNamesExterior, colorNamesInterior } from "./util/colorNames";

const wheelText = ['Standard 19"', 'AT 21"'];

const ColorPicker = ({
  DisplayMode,
  selectedColor,
  ColorsArr,
  handleColorChange,
}) => {
  return (
    <div className="color-picker">
      <div className="title">
        {DisplayMode === "interior"
          ? colorNamesInterior[selectedColor.id]
          : colorNamesExterior[selectedColor.id]}
      </div>
      <div className="colors">
        {ColorsArr.map((color, i) => {
          const selected = selectedColor.id == i;

          return (
            <div
              key={"colors" + i}
              id={i}
              className={`color ${selected ? "selected" : ""}`}
              onClick={(e) => handleColorChange(e, color, i)}
              style={{
                background: Array.isArray(color)
                  ? `linear-gradient(to right bottom, ${color[0]} 50%, ${color[1]} 50%)`
                  : color,
              }}
            >
              <MotionConfig transition={{ duration: 0.3 }}>
                {selectedColor.id == i && (
                  <motion.img
                    key={"color-picker-checkmark_" + i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    id={i}
                    style={{
                      display: "block",
                      marginLeft: "auto",
                      marginRight: "auto",
                      filter: i == 2 && "grayscale(1) invert(1)",
                      width: "35%",
                      height: "100%",
                    }}
                    src={checkmark}
                  />
                )}
              </MotionConfig>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WheelPicker = ({
  wheelText,
  WheelsArr,
  WheelIndex,
  handleWheelChange,
}) => {
  return (
    <div className="wheel-picker">
      <div className="title">Select a wheel {!isMobile && "type"}</div>
      <div className="wheels">
        {WheelsArr.map((wheel, i) => {
          return (
            <div
              key={"wheel" + i}
              id={i}
              onClick={(e) => handleWheelChange(e, i)}
              className={`wheel ${WheelIndex == i ? "selected" : ""}`}
            >
              <div
                style={{
                  width: isMobile ? "28px" : "40px",
                  borderRadius: "50%",
                  height: isMobile ? "28px" : "40px",
                  backgroundImage: `url(${wheel})`,
                  backgroundPosition: "center",
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  pointerEvents: "auto",
                  cursor: "pointer",
                }}
              />
              {!isMobile && <span>{wheelText[i]}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ColorAndWheelPicker = () => {
  const [showWheelPicker, setShowWheelPicker] = useState(false);
  const model = useCustomizerStore((state) => state.model);
  const DisplayMode = useTransitionStore((state) => state.DisplayMode);
  const setTrimColor = useCustomizerStore((state) => state.setTrimColor);
  const setWheelIndex = useTransitionStore((state) => state.setWheelIndex);
  const selectedHotspot = useCustomizerStore((state) => state.selectedHotspot);
  const selectionByModel = useCustomizerStore(
    (state) => state.selectionByModel
  );
  const [ColorsArr, setColorsArr] = useState(
    fetchCustomizerData()[model].colors
  );
  const [WheelsArr, setWheelsArr] = useState(
    fetchCustomizerData()[model].wheels.colors
  );
  const selectedColorId = getSelectedColorIndex(model, DisplayMode);
  const selectedWheelId = getSelectedWheelIndex(model);
  const selectedColor = { id: selectedColorId };

  const handleColorChange = useCallback((e, color, i) => {
    setSelectedColorIndex(model, i, DisplayMode);

    if (DisplayMode == "interior") {
      useCustomizerStore.getState().setInteriorSelection(model, i);
    } else {
      setTrimColor(color, model);
    }
  }, [DisplayMode, model, setTrimColor]);

  const handleWheelChange = useCallback((e, i) => {
    setSelectedWheelIndex(model, i);

    setWheelIndex(i);
  }, [model, setWheelIndex]);

  const handleDisplayChanges = useCallback(() => {
    if (DisplayMode === "flipper") return;

    setColorsArr(fetchCustomizerData()[model][`${DisplayMode}`].colors);
    setWheelsArr(fetchCustomizerData()[model].wheels.colors);
    setWheelIndex(getSelectedWheelIndex(model));
  }, [DisplayMode, model, setWheelIndex]);

  useEffect(() => {
    handleDisplayChanges();
  }, [DisplayMode, model, selectionByModel, handleDisplayChanges]);

  useEffect(() => {
    if (model == 1 && DisplayMode === "exterior" && selectedHotspot == 1) {
      setShowWheelPicker(true);
    } else setShowWheelPicker(false);
  }, [model, DisplayMode, selectedHotspot]);

  return (
    <MotionConfig transition={{ duration: 1 }}>
      <AnimatePresence>
        <ColorPickerContainer
          key="color-picker-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          isMobile={isMobile}
          spaceBetween={showWheelPicker}
        >
          {showWheelPicker && !isMobile && (
            <WheelPicker
              wheelText={wheelText}
              WheelsArr={WheelsArr}
              WheelIndex={selectedWheelId}
              handleWheelChange={handleWheelChange}
            />
          )}

          <ColorPicker
            DisplayMode={DisplayMode}
            selectedColor={selectedColor}
            ColorsArr={ColorsArr}
            handleColorChange={handleColorChange}
          />

          {showWheelPicker && isMobile && (
            <WheelPicker
              wheelText={wheelText}
              WheelsArr={WheelsArr}
              WheelIndex={selectedWheelId}
              handleWheelChange={handleWheelChange}
            />
          )}
        </ColorPickerContainer>
      </AnimatePresence>
    </MotionConfig>
  );
};

const ColorPickerContainer = styled(motion.div)`
  position: absolute;
  bottom: ${({ isMobile }) =>
    isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 64px)" : "0"};
  right: 0;
  z-index: 999;
  display: flex;
  margin: ${({ isMobile }) => (isMobile ? "0 15px 0 0" : "60px")};
  gap: ${({ isMobile }) => (isMobile ? "15px" : "40px")};
  width: ${({ isMobile }) => (isMobile ? "calc(100% - 30px)" : "auto")};
  justify-content: ${({ isMobile, spaceBetween }) =>
    isMobile ? (spaceBetween ? "space-between" : "flex-start") : "center"};

  @supports (bottom: 1dvh) {
    bottom: ${({ isMobile }) =>
      isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 8dvh)" : "0"};
  }

  .color-picker,
  .wheel-picker {
    display: flex;
    flex-direction: column;
    gap: ${({ isMobile }) => (isMobile ? "15px" : "20px")};

    .title {
      color: #fff;
      text-align: ${({ isMobile }) => (isMobile ? "left" : "center")};
      text-transform: uppercase;
      font-size: ${({ isMobile }) => (isMobile ? "13px" : "16px")};
      text-align: ${({ isMobile }) => (isMobile ? "left" : "right")};
    }

    .colors {
      gap: ${({ isMobile }) => (isMobile ? "10px" : "15px")};
      justify-content: ${({ isMobile }) =>
        isMobile ? "flex-start" : "flex-end"};
    }

    .wheels {
      gap: ${({ isMobile }) => (isMobile ? "14px" : "25px")};
      justify-content: ${({ isMobile }) =>
        isMobile ? "flex-start" : "flex-end"};
    }

    .colors,
    .wheels {
      display: flex;
      color: #fff;

      .color {
        width: ${({ isMobile }) => (isMobile ? "28px" : "40px")};
        border-radius: 50%;
        height: ${({ isMobile }) => (isMobile ? "28px" : "40px")};
        pointer-events: auto;
        cursor: pointer;

        ::before {
          width: ${({ isMobile }) => (isMobile ? "28px" : "40px")};
          height: ${({ isMobile }) => (isMobile ? "28px" : "40px")};
          content: "";
          position: absolute;
          top: -2px;
          right: -2px;
          bottom: -2px;
          left: -2px;
          border: 2px solid #fff;
          border-radius: 50%;
          transition: all 0.2s ease-in-out;
        }

        &.selected::before {
          width: ${({ isMobile }) => (isMobile ? "32px" : "44px")};
          height: ${({ isMobile }) => (isMobile ? "32px" : "44px")};
          top: -4px;
          right: -4px;
          bottom: -4px;
          left: -4px;
          transition: all 0.2s ease-in-out;
        }
      }

      .wheel {
        display: flex;
        align-items: center;
        gap: 15px;
        border-radius: 50%;
        pointer-events: auto;
        cursor: pointer;

        ::before {
          width: ${({ isMobile }) => (isMobile ? "28px" : "40px")};
          height: ${({ isMobile }) => (isMobile ? "28px" : "40px")};
          content: "";
          position: absolute;
          top: -2px;
          right: -2px;
          bottom: -3px;
          left: -2px;
          border: 2px solid #fff;
          border-radius: 50%;
          transition: all 0.2s ease-in-out;
        }

        &.selected::before {
          width: ${({ isMobile }) => (isMobile ? "32px" : "46px")};
          height: ${({ isMobile }) => (isMobile ? "32px" : "46px")};
          top: -4px;
          right: -4px;
          bottom: -4px;
          left: -4px;
          transition: all 0.2s ease-in-out;
        }

        & > * {
          pointer-events: none;
        }
      }

      .color,
      .wheel {
        transition: all 0.2s ease-in-out;
        position: relative;
      }

      .color {
        &:hover {
          transform: scale(1.05);
        }
      }

      .wheel {
        &:hover {
          &::before,
          div {
            transform: scale(1.05);
          }
        }
      }
    }
  }
`;

export default React.memo(ColorAndWheelPicker);
